import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGridPro, gridClasses } from "@mui/x-data-grid-pro";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import useSGMList from "../../hooks/useSGMList";
import useGroupedBet from "../../hooks/useGroupedBet";
import useMarketGroupings from "../../hooks/useMarketGroupings";
import useUserFilters from "../../hooks/useUserFilters";
import { EnhancedErrorHandler } from "../EnhancedErrorHandler";
import GroupedBetModal from "../ValueBets/GroupedBetModal";
import BookieWeightsModal from "../ValueBets/BookieWeightsModal";

// Import SGM components
import SGMHeader from "./SGMHeader";
import SGMFilters from "./SGMFilters";
import SGMBuilder from "./SGMBuilder";
import SGMColumns from "./SGMColumns";

// Calculate combined odds for SGM
const calculateCombinedOdds = (selections) => {
  if (!selections.length) return 0;
  // Multiply all odds together
  return selections.reduce((acc, selection) => acc * selection.rate, 1);
};

const SGMPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Bookie weights state and user filters hook
  const [bookieWeights, setBookieWeights] = useState(null);
  const [isBookieWeightsModalOpen, setIsBookieWeightsModalOpen] =
    useState(false);
  const {
    filters: bookieWeightFilters,
    loading: bookieWeightFiltersLoading,
    error: bookieWeightFiltersError,
    fetchFilters: fetchBookieWeights,
    saveFilter: saveBookieWeights,
  } = useUserFilters();

  // Format bookie weights for API calls
  const getBookieWeightsParam = useCallback(() => {
    if (!bookieWeights || Object.keys(bookieWeights).length === 0) {
      return "";
    }
    return Object.entries(bookieWeights)
      .map(([bookie, weight]) => `${bookie}:${weight}`)
      .join(",");
  }, [bookieWeights]);

  const { sgmBets, loading, error, triggerRefresh, filterOptions } = useSGMList(
    eventId,
    getBookieWeightsParam()
  );

  // Get related events data
  const { data: eventData, loading: eventDataLoading } =
    useMarketGroupings(eventId);

  // Menu state for related events dropdown
  const [anchorEl, setAnchorEl] = useState(null);
  const handleRelatedClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleRelatedClose = () => {
    setAnchorEl(null);
  };
  const handleRelatedSelect = (relatedEventId) => {
    handleRelatedClose();
    navigate(`/sgm/${relatedEventId}`);
  };

  // Log event data for debugging
  useEffect(() => {
    if (eventData) {
      console.log("SGM Page - Event Data:", eventData);
      console.log("Related Events:", eventData.relatedEvents?.length || 0);
    }
  }, [eventData]);

  // Fetch user filters for bookie weights
  useEffect(() => {
    fetchBookieWeights("value-betting", "bookieWeightConfig");
  }, [fetchBookieWeights]);

  // Set bookie weights from user filters
  useEffect(() => {
    if (!!bookieWeightFilters && !!bookieWeightFilters.content) {
      setBookieWeights(bookieWeightFilters.content);
    }
  }, [bookieWeightFilters]);

  // Bookie weights modal handlers
  const handleOpenBookieWeightsModal = () => {
    setIsBookieWeightsModalOpen(true);
  };

  const handleCloseBookieWeightsModal = () => {
    setIsBookieWeightsModalOpen(false);
  };

  // Add a handler for when bookie weights are saved
  const handleSaveBookieWeights = (weights) => {
    setBookieWeights(weights);

    // Save the weights using the specialized hook
    saveBookieWeights({
      key: "bookieWeightConfig",
      content: weights,
      category: "value-betting",
    });

    // If there's a grouped bet selected, refresh it with the new weights
    if (selectedGroupedBetId) {
      const weightsParam = Object.entries(weights)
        .map(([bookie, weight]) => `${bookie}:${weight}`)
        .join(",");
      // Determine evMode based on whether fair odds exists
      const hasFairOdds =
        selectedGroupedBet?.fairOdds && selectedGroupedBet?.fairOdds > 0;
      fetchGroupedBet(selectedGroupedBetId, hasFairOdds, [], weightsParam);
    }
  };

  // Event info
  const [eventName, setEventName] = useState("");
  const [eventDateTime, setEventDateTime] = useState("");
  const [formattedDateTime, setFormattedDateTime] = useState("");
  const [currentEventId, setCurrentEventId] = useState(null);

  // Filter states
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [selectedBookies, setSelectedBookies] = useState(() => {
    const bookie = searchParams.get("bookie");
    return bookie ? [bookie] : [];
  });
  const [maxOdds, setMaxOdds] = useState("");
  const [minOdds, setMinOdds] = useState(() => {
    const storedValue = localStorage.getItem("sgmMinOdds");
    return storedValue || "";
  });
  const [minBookieCount, setMinBookieCount] = useState(() => {
    const storedValue = localStorage.getItem("sgmMinBookieCount");
    return storedValue || "";
  });

  // Fair Odds Only filter toggle state - initialize from localStorage
  const [fairOddsOnly, setFairOddsOnly] = useState(() => {
    const storedValue = localStorage.getItem("sgmFairOddsOnly");
    return storedValue ? JSON.parse(storedValue) : false;
  });

  // Column width state
  const [columnWidths, setColumnWidths] = useState(() => {
    // Try to get stored widths first
    const storedWidths = localStorage.getItem("sgmPageColumnWidths");
    if (storedWidths) {
      try {
        return JSON.parse(storedWidths);
      } catch (e) {
        console.error("Failed to parse stored column widths");
      }
    }

    // Calculate default widths based on viewport if no stored widths
    const containerWidth = window.innerWidth - 48; // Account for padding
    const defaultWidths = {};
    // Define default column width percentages
    const columnWidthPercentages = {
      market: 15,
      key: 15,
      bookie: 10,
      bookieCount: 5,
      rate: 6,
      bestAltOdds: 6,
      aveOdds: 6,
      fairOdds: 6,
      ev: 7,
      oddsAveRatio: 7,
      firstSecondRatio: 7,
      actions: 5,
      eventStart: 13,
    };

    Object.entries(columnWidthPercentages).forEach(([field, percentage]) => {
      defaultWidths[field] = Math.floor((containerWidth * percentage) / 100);
    });
    return defaultWidths;
  });

  // Handle column width changes
  const handleColumnWidthChange = useCallback(
    (params) => {
      const newWidths = {
        ...columnWidths,
        [params.colDef.field]: params.width,
      };
      setColumnWidths(newWidths);
      localStorage.setItem("sgmPageColumnWidths", JSON.stringify(newWidths));
    },
    [columnWidths]
  );

  // GroupedBetModal state
  const [selectedGroupedBetId, setSelectedGroupedBetId] = useState(null);
  const [selectedGroupedBet, setSelectedGroupedBet] = useState(null);
  const {
    groupedBetData,
    loading: groupedBetLoading,
    error: groupedBetError,
    fetchGroupedBet,
  } = useGroupedBet();

  // Sorting state
  const [sortModel, setSortModel] = useState([
    {
      field: "ev",
      sort: "desc",
    },
  ]);

  // SGM Builder state
  const [sgmSelections, setSgmSelections] = useState([]);
  const [sgmBuilderOpen, setSgmBuilderOpen] = useState(true);

  // Calculated combined odds
  const combinedOdds = useMemo(() => {
    return calculateCombinedOdds(sgmSelections);
  }, [sgmSelections]);

  // Add selection to SGM builder
  const addToSGMBuilder = useCallback(
    (bet) => {
      // Create a unique key for selections that includes market, selection, and bookie
      // This allows same selections from different bookies
      const selectionKey = `${bet.market}:${bet.key}:${bet.bookie}`;

      // Check if we already have this exact selection in the builder
      const selectionExists = sgmSelections.some(
        (selection) =>
          `${selection.market}:${selection.selection}:${selection.bookie}` ===
          selectionKey
      );

      if (selectionExists) {
        // If the exact selection already exists, replace it
        setSgmSelections((prevSelections) =>
          prevSelections.map((selection) =>
            `${selection.market}:${selection.selection}:${selection.bookie}` ===
            selectionKey
              ? {
                  id: bet.id,
                  market: bet.market,
                  selection: bet.key,
                  rate: bet.rate,
                  bookie: bet.bookie,
                  selectionKey,
                }
              : selection
          )
        );
      } else {
        // Add as a new selection
        setSgmSelections((prevSelections) => [
          ...prevSelections,
          {
            id: bet.id,
            market: bet.market,
            selection: bet.key,
            rate: bet.rate,
            bookie: bet.bookie,
            selectionKey,
          },
        ]);
      }
    },
    [sgmSelections]
  );

  // Remove selection from SGM builder
  const removeFromSGMBuilder = useCallback((selectionId) => {
    setSgmSelections((prevSelections) =>
      prevSelections.filter((selection) => selection.id !== selectionId)
    );
  }, []);

  // Clear all selections
  const clearSGMBuilder = useCallback(() => {
    setSgmSelections([]);
  }, []);

  // Toggle SGM builder panel
  const toggleSGMBuilder = useCallback(() => {
    setSgmBuilderOpen((prev) => !prev);
  }, []);

  // Process the bets to add unique IDs for DataGrid
  const processedBets = useMemo(() => {
    return sgmBets.map((bet) => ({
      ...bet,
      id: uuidv4(),
      evFormatted: bet.ev !== undefined ? `${bet.ev.toFixed(2)}%` : "N/A",
      oddsAveRatioFormatted:
        bet.oddsAveRatio !== undefined
          ? `${((bet.oddsAveRatio - 1) * 100).toFixed(2)}%`
          : "N/A",
      firstSecondRatioFormatted:
        bet.firstSecondRatio !== undefined
          ? `${((bet.firstSecondRatio - 1) * 100).toFixed(2)}%`
          : "N/A",
      oddsFairRatioFormatted:
        bet.oddsFairRatio !== undefined
          ? `${((bet.oddsFairRatio - 1) * 100).toFixed(2)}%`
          : "N/A",
    }));
  }, [sgmBets]);

  // Filter rows based on selected markets, bookies, max odds, min odds, min bookie count, and fair odds only
  const filteredRows = useMemo(() => {
    if (
      !selectedMarkets.length &&
      !selectedBookies.length &&
      !maxOdds &&
      !minOdds &&
      !minBookieCount &&
      !fairOddsOnly
    )
      return processedBets;

    return processedBets.filter((bet) => {
      const marketMatch =
        !selectedMarkets.length || selectedMarkets.includes(bet.market);
      const bookieMatch =
        !selectedBookies.length || selectedBookies.includes(bet.bookie);
      const maxOddsMatch =
        !maxOdds || (bet.rate && bet.rate <= parseFloat(maxOdds));
      const minOddsMatch =
        !minOdds || (bet.rate && bet.rate >= parseFloat(minOdds));
      const minBookieCountMatch =
        !minBookieCount ||
        (bet.bookieCount && bet.bookieCount >= parseInt(minBookieCount));
      const fairOddsMatch = !fairOddsOnly || (bet.fairOdds && bet.fairOdds > 0);

      return (
        marketMatch &&
        bookieMatch &&
        maxOddsMatch &&
        minOddsMatch &&
        minBookieCountMatch &&
        fairOddsMatch
      );
    });
  }, [
    processedBets,
    selectedMarkets,
    selectedBookies,
    maxOdds,
    minOdds,
    minBookieCount,
    fairOddsOnly,
  ]);

  // Get sorted markets and bookies for filters
  const sortedMarkets = useMemo(() => {
    if (!filterOptions.markets) return [];
    return [...filterOptions.markets].sort();
  }, [filterOptions.markets]);

  const sortedBookies = useMemo(() => {
    if (!filterOptions.bookies) return [];
    return [...filterOptions.bookies].sort();
  }, [filterOptions.bookies]);

  // Handle filter changes
  const handleMarketChange = useCallback((event) => {
    const {
      target: { value },
    } = event;
    setSelectedMarkets(typeof value === "string" ? value.split(",") : value);
  }, []);

  const handleBookieChange = useCallback((event) => {
    const {
      target: { value },
    } = event;
    setSelectedBookies(typeof value === "string" ? value.split(",") : value);
  }, []);

  const handleMaxOddsChange = useCallback((event) => {
    const value = event.target.value;
    // Allow empty or valid numbers
    if (value === "" || (!isNaN(value) && parseFloat(value) > 0)) {
      setMaxOdds(value);
    }
  }, []);

  const handleMinOddsChange = useCallback((event) => {
    const value = event.target.value;
    // Allow empty or valid numbers greater than 1
    if (value === "" || (!isNaN(value) && parseFloat(value) >= 1)) {
      setMinOdds(value);
      localStorage.setItem("sgmMinOdds", value);
    }
  }, []);

  const handleMinBookieCountChange = useCallback((event) => {
    const value = event.target.value;
    // Allow empty or valid numbers greater than 0
    if (value === "" || (!isNaN(value) && parseInt(value) >= 1)) {
      setMinBookieCount(value);
      localStorage.setItem("sgmMinBookieCount", value);
    }
  }, []);

  // Handle fair odds toggle change
  const handleFairOddsToggle = useCallback((event) => {
    const newValue = event.target.checked;
    setFairOddsOnly(newValue);
    localStorage.setItem("sgmFairOddsOnly", JSON.stringify(newValue));
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedMarkets([]);
    setSelectedBookies([]);
    setMaxOdds("");
    setMinOdds("");
    setMinBookieCount("");
    setFairOddsOnly(false);
    localStorage.setItem("sgmFairOddsOnly", JSON.stringify(false));
    localStorage.setItem("sgmMinBookieCount", "");
  }, []);

  // Handle opening GroupedBetModal
  const handleGroupedBetClick = useCallback(
    (row) => {
      // Determine evMode based on whether fair odds exists
      const hasFairOdds = row.fairOdds && row.fairOdds > 0;

      // Set the grouped bet data
      setSelectedGroupedBet(row);
      setSelectedGroupedBetId(row.groupedBetId);

      // Fetch the grouped bet data with the correct evMode
      fetchGroupedBet(
        row.groupedBetId,
        hasFairOdds,
        [],
        getBookieWeightsParam()
      );
    },
    [fetchGroupedBet, getBookieWeightsParam]
  );

  // Get event details when data is first loaded
  useEffect(() => {
    if (sgmBets.length > 0) {
      setEventName(sgmBets[0].event || "Event Details");
      if (sgmBets[0].eventStart) {
        setEventDateTime(sgmBets[0].eventStart);

        // Format the date/time in a user-friendly way
        const eventDate = moment(sgmBets[0].eventStart);
        let formattedDate = "";

        if (moment().isSame(eventDate, "day")) {
          formattedDate = `Today ${eventDate.format("h:mm A")}`;
        } else if (moment().add(1, "day").isSame(eventDate, "day")) {
          formattedDate = `Tomorrow ${eventDate.format("h:mm A")}`;
        } else if (moment().add(7, "day").isAfter(eventDate)) {
          formattedDate = `${eventDate.format("dddd")} ${eventDate.format(
            "h:mm A"
          )}`;
        } else {
          formattedDate = `${eventDate.format(
            "dddd, MMMM Do"
          )} ${eventDate.format("h:mm A")}`;
        }

        setFormattedDateTime(formattedDate);
      }
      // Set the event_id
      if (sgmBets[0].event_id) {
        setCurrentEventId(sgmBets[0].event_id);
      }
    }
  }, [sgmBets]);

  const handleBack = () => {
    navigate("/");
  };

  const copyToBetTracker = (bet) => {
    const { eventStart, bookie, event, key, rate, market } = bet;
    const formattedDate = moment(eventStart).format("YYYY-MM-DD");
    const formattedBookie = bookie === "Neds Group" ? "Neds" : bookie;
    const betTrackerData = `${formattedDate}\t${formattedBookie}\tMug\t\t${event}\t${market} ${key}\t\t${rate}`;
    navigator.clipboard.writeText(betTrackerData);
  };

  // Custom styling for the DataGrid
  const customGridStyles = {
    border: 0,
    color: theme.palette.text.primary,
    "& .MuiDataGrid-row:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "& .MuiDataGrid-columnHeaders": {
      borderBottom: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 700,
    },
    "& .MuiDataGrid-cell": {
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: "8px 16px",
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    // Style for high EV rows
    "& .high-ev-row": {
      backgroundColor: "rgba(53, 189, 91, 0.05)",
    },
    // Style for medium EV rows
    "& .medium-ev-row": {
      backgroundColor: "rgba(255, 165, 0, 0.05)",
    },
    [`& .${gridClasses.cell}`]: {
      py: "8px",
    },
    // Custom loading overlay style
    "& .MuiDataGrid-loadingOverlay": {
      backgroundColor: "rgba(255, 255, 255, 0.7)",
    },
  };

  // Custom row styling based on EV value
  const getRowClassName = (params) => {
    if (params.row.ev >= 3) return "high-ev-row";
    if (params.row.ev > 0) return "medium-ev-row";
    return "";
  };

  // Get row height for mobile
  const getRowHeight = useCallback(() => {
    return isMobile ? "auto" : null;
  }, [isMobile]);

  // Get active filter count
  const activeFilterCount = useMemo(() => {
    let count = selectedMarkets.length + selectedBookies.length;
    if (maxOdds) count++;
    if (minOdds) count++;
    if (minBookieCount) count++;
    if (fairOddsOnly) count++;
    return count;
  }, [
    selectedMarkets,
    selectedBookies,
    maxOdds,
    minOdds,
    minBookieCount,
    fairOddsOnly,
  ]);

  // Get columns from SGMColumns component
  const { columnsWithSGMAction } = SGMColumns({
    columnWidths,
    handleColumnWidthChange,
    handleGroupedBetClick,
    copyToBetTracker,
    addToSGMBuilder,
    sgmSelections,
    isMobile,
  });

  return (
    <Box sx={{ m: 3 }}>
      {/* Header */}
      <SGMHeader
        eventName={eventName}
        eventDateTime={eventDateTime}
        eventData={{ ...eventData, eventId: currentEventId || eventId }}
        handleBack={handleBack}
        triggerRefresh={triggerRefresh}
        loading={loading}
        anchorEl={anchorEl}
        handleRelatedClick={handleRelatedClick}
        handleRelatedClose={handleRelatedClose}
        handleRelatedSelect={handleRelatedSelect}
        onOpenBookieWeightsModal={handleOpenBookieWeightsModal}
        hasCustomBookieWeights={
          bookieWeights && Object.keys(bookieWeights).length > 0
        }
      />

      {/* Create a flex container to hold filters and SGM builder side by side */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          mb: 3,
          flexDirection: isMobile ? "column" : "row",
          width: "100%",
        }}
      >
        {/* Filters section */}
        <SGMFilters
          selectedMarkets={selectedMarkets}
          selectedBookies={selectedBookies}
          maxOdds={maxOdds}
          minOdds={minOdds}
          minBookieCount={minBookieCount}
          fairOddsOnly={fairOddsOnly}
          sortedMarkets={sortedMarkets}
          sortedBookies={sortedBookies}
          handleMarketChange={handleMarketChange}
          handleBookieChange={handleBookieChange}
          handleMaxOddsChange={handleMaxOddsChange}
          handleMinOddsChange={handleMinOddsChange}
          handleMinBookieCountChange={handleMinBookieCountChange}
          handleFairOddsToggle={handleFairOddsToggle}
          clearFilters={clearFilters}
          activeFilterCount={activeFilterCount}
          filteredRows={filteredRows}
          processedBets={processedBets}
        />

        {/* SGM Builder Panel */}
        <SGMBuilder
          sgmSelections={sgmSelections}
          sgmBuilderOpen={sgmBuilderOpen}
          toggleSGMBuilder={toggleSGMBuilder}
          clearSGMBuilder={clearSGMBuilder}
          removeFromSGMBuilder={removeFromSGMBuilder}
          combinedOdds={combinedOdds}
          eventDateTime={eventDateTime}
          eventName={eventName}
        />
      </Box>

      <EnhancedErrorHandler error={error} />

      <Box sx={{ width: "100%", height: isMobile ? "auto" : "80vh" }}>
        <DataGridPro
          rows={filteredRows}
          columns={columnsWithSGMAction}
          loading={loading}
          pageSize={25}
          rowsPerPageOptions={[25, 50, 100]}
          getRowId={(row) => row.id}
          sortingOrder={["desc", "asc"]}
          getRowClassName={getRowClassName}
          getRowHeight={getRowHeight}
          autoHeight={isMobile}
          pagination
          disableColumnFilter={isMobile}
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          onColumnWidthChange={handleColumnWidthChange}
          disableColumnSelector={isMobile}
          disableDensitySelector={isMobile}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          slotProps={{
            loadingOverlay: {
              variant: "skeleton",
            },
            toolbar: {
              showQuickFilter: !isMobile,
              quickFilterProps: { debounceMs: 500 },
            },
            row: {
              selectionFollowsFocus: false,
            },
          }}
          slots={{
            noRowsOverlay: () => (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Typography variant="body1">
                  No Same Game Multi opportunities found for this event.
                </Typography>
              </Box>
            ),
            loadingOverlay: (props) => (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 99,
                }}
              >
                <CircularProgress />
              </Box>
            ),
          }}
          sx={customGridStyles}
        />
      </Box>

      {/* GroupedBetModal */}
      <GroupedBetModal
        open={!!selectedGroupedBetId}
        onClose={() => setSelectedGroupedBetId(null)}
        bet={selectedGroupedBet?.bet}
        fullBet={selectedGroupedBet?.key}
        league={selectedGroupedBet?.league}
        market={selectedGroupedBet?.market}
        event={selectedGroupedBet?.event}
        event_id={selectedGroupedBet?.event_id}
        groupedBetData={groupedBetData}
        loading={groupedBetLoading}
        error={groupedBetError}
        evMode={
          selectedGroupedBet?.fairOdds && selectedGroupedBet?.fairOdds > 0
        }
      />

      {/* BookieWeightsModal */}
      <BookieWeightsModal
        open={isBookieWeightsModalOpen}
        onClose={handleCloseBookieWeightsModal}
        onSave={handleSaveBookieWeights}
      />
    </Box>
  );
};

export default SGMPage;
